import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Avatar,
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Heading,
  HStack,
  Icon,
  Progress,
  Select,
  Skeleton,
  Spinner,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tooltip,
  Tr,
  VStack,
} from "@chakra-ui/react";
import {
  MdAutoAwesome,
  MdChevronRight,
  MdInfoOutline,
  MdOutlineFlag,
  MdOutlineLightbulb,
  MdOutlineWorkspacePremium,
  MdTrendingDown,
  MdTrendingFlat,
  MdTrendingUp,
} from "react-icons/md";
import { useLocation, useNavigate } from "react-router-dom";
import {
  getManagerCoachingDashboard,
  refreshManagerCoachingSummary,
} from "../../services/managerEngagement";
import {
  comparisonPeriodDescription,
  stageDisplayLabel,
} from "./reportingPeriod";
import NarrativeCitation from "./NarrativeCitation";

const JOB_LABELS = {
  sales: "Sales",
  service_advisor: "Service",
  finance: "Finance",
};

const SUMMARY_POLL_INTERVAL_MS = 4000;
const SUMMARY_POLL_LIMIT = 15;

function percentage(value) {
  return value == null ? "—" : `${Math.round(Number(value))}%`;
}

// Mean conversation talk-time, formatted m:ss (e.g. 214000 -> "3:34").
function formatDuration(ms) {
  if (ms == null) return "—";
  const totalSeconds = Math.round(Number(ms) / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

const SHORT_CONVERSATION_TOOLTIP =
  "Conversations under 2 minutes are shown but excluded from the coaching score.";

// Muted accountability chip: short (under 2 min) conversations are surfaced but
// excluded from the coaching score server-side, so this reads as context, not a score.
function ShortConversationChip({ count }) {
  if (!Number(count)) return null;
  return (
    <Tooltip label={SHORT_CONVERSATION_TOOLTIP} hasArrow>
      <Badge
        variant="subtle"
        colorScheme="gray"
        color="gray.500"
        bg="gray.100"
        borderRadius="full"
        px={2.5}
        py={0.5}
        textTransform="none"
        fontWeight="medium"
      >
        {count} short {Number(count) === 1 ? "conversation" : "conversations"} · not scored
      </Badge>
    </Tooltip>
  );
}

function trendLabel(value) {
  if (value == null) return "Not enough history";
  return `${value > 0 ? "+" : ""}${Number(value).toFixed(1)} pts`;
}

const RATING_CONFIG = {
  gold: { label: "Gold rating", color: "#C49A00", bg: "#FFF7D1", glow: "0 0 18px rgba(229, 187, 48, .45)" },
  silver: { label: "Silver rating", color: "#858585", bg: "#F1F1F1", glow: "0 0 16px rgba(122, 122, 122, .28)" },
  bronze: { label: "Bronze rating", color: "#A45B18", bg: "#F2D8C2", glow: "0 0 16px rgba(191, 128, 40, .34)" },
  opportunity: { label: "Coaching opportunity", color: "#D14343", bg: "#FDE6E6", glow: "none" },
  sample: { label: "Building sample", color: "#667085", bg: "#F2F4F7", glow: "none" },
};

function ratingFor(score) {
  if (score == null) return "sample";
  if (score >= 85) return "gold";
  if (score >= 70) return "silver";
  if (score >= 55) return "bronze";
  return "opportunity";
}

function Rating({ score, compact = false }) {
  const config = RATING_CONFIG[ratingFor(score)];
  const icon = (
    <HStack spacing={2}>
      <Flex
        boxSize={compact ? "28px" : "36px"}
        borderRadius="full"
        bg={config.bg}
        color={config.color}
        align="center"
        justify="center"
        boxShadow={config.glow}
        flex="0 0 auto"
      >
        <Icon as={MdOutlineWorkspacePremium} boxSize={compact ? 4 : 5} />
      </Flex>
      {!compact && <Text color="gray.700" fontWeight="bold" fontSize="md">{config.label}</Text>}
    </HStack>
  );
  return compact ? <Tooltip label={config.label} hasArrow>{icon}</Tooltip> : icon;
}

function MetricOverviewCard({ dimension, period, onOpen }) {
  const comparison = comparisonPeriodDescription(period);
  return (
    <Box
      as="button"
      type="button"
      textAlign="left"
      bg="white"
      borderWidth="1px"
      borderColor="gray.200"
      borderRadius="xl"
      p={4}
      minH="152px"
      boxShadow="0 1px 2px rgba(16,24,40,.04)"
      onClick={onOpen}
      _hover={{ borderColor: "blue.300", boxShadow: "0 8px 24px rgba(16,24,40,.08)", transform: "translateY(-1px)" }}
      _focusVisible={{ outline: "2px solid", outlineColor: "blue.500", outlineOffset: "2px" }}
      transition="all .16s ease"
    >
      <Flex justify="space-between" gap={2} align="start">
        <Text fontWeight="bold" color="gray.700" noOfLines={2}>{stageDisplayLabel(dimension.label)}</Text>
        <Tooltip label={comparison} hasArrow>
          <Badge colorScheme={dimension.trend > 0 ? "green" : dimension.trend < 0 ? "red" : "gray"}>
            {dimension.trend == null ? "—" : trendLabel(dimension.trend)}
          </Badge>
        </Tooltip>
      </Flex>
      <Box mt={3}><Rating score={dimension.score} /></Box>
      <Flex justify="space-between" align="end" mt={3}>
        <Box flex="1" mr={4}>
          <Progress value={dimension.score || 0} size="sm" borderRadius="full" colorScheme="blue" bg="blue.50" />
          <Text mt={1.5} fontSize="xs" color="gray.500">{dimension.scorecard_count} customer conversations</Text>
        </Box>
        <Text fontWeight="bold" fontSize="2xl" color="gray.900">{percentage(dimension.score)}</Text>
      </Flex>
    </Box>
  );
}

function anchorPart(value) {
  return String(value || "item").toLowerCase().replace(/[^a-z0-9_-]+/g, "-").replace(/^-+|-+$/g, "");
}

function InsightCard({ title, items, icon, accent, empty, onOpenEvidence }) {
  return (
    <Box bg="#F7F7F8" borderWidth="1px" borderColor="gray.200" borderRadius="lg" p={4}>
      <HStack spacing={2.5} mb={3}>
        <Icon as={icon} color={accent} boxSize={5} />
        <Text fontWeight="bold" color="gray.800">{title}</Text>
      </HStack>
      <VStack align="stretch" spacing={3}>
        {(items || []).slice(0, 3).map((item, index) => (
          <Box key={`${title}-${index}`} borderTopWidth={index ? "1px" : 0} borderColor="gray.200" pt={index ? 3 : 0}>
            <Text fontSize="sm" color="gray.700" lineHeight="1.55">{item.text}</Text>
            {(item.citations || []).slice(0, 2).map((citation, citationIndex) => {
              const anchorId = `manager-coaching-citation-${anchorPart(title)}-${index}-${citationIndex}-${anchorPart(citation.evidence_id)}`;
              return (
                <NarrativeCitation
                  key={`${citation.evidence_id}-${citationIndex}`}
                  citation={citation}
                  anchorId={anchorId}
                  onOpen={(value) => onOpenEvidence?.(value, anchorId)}
                />
              );
            })}
          </Box>
        ))}
        {!items?.length && <Text fontSize="sm" color="gray.500">{empty}</Text>}
      </VStack>
    </Box>
  );
}

export default function CoachingPerformanceDashboard({
  periodId,
  jobClass,
  frameworkFamily,
  reportingGroupId,
  locationId,
  onJobClassChange,
  onFrameworkFamilyChange,
  onReportingGroupChange,
  onLocationChange,
  refreshVersion = 0,
}) {
  const navigate = useNavigate();
  const location = useLocation();
  // The production dashboard controls these values through the URL. Keep a
  // local fallback so the component remains usable in isolation (including
  // story/test harnesses) without creating a second source of truth in-app.
  const [localJobClass, setLocalJobClass] = useState(jobClass || "");
  const [localFrameworkFamily, setLocalFrameworkFamily] = useState(frameworkFamily || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [available, setAvailable] = useState(true);
  const [summaryStatus, setSummaryStatus] = useState("idle");
  const summaryPollCountRef = useRef(0);
  const summaryPollInFlightRef = useRef(false);
  // For arbitrary ranges the backend does not pre-generate a narrative; it is
  // produced only when the manager explicitly asks. This ref records that the
  // manager clicked "Summarize this range" for the current scope so the auto
  // gate opens and the poll keeps running until the summary lands.
  const summaryRequestedRef = useRef(false);
  // Tracks the scope the on-request gate belongs to, so the gate is cleared
  // when (and only when) the actual scope changes — never by incidental reloads.
  const summaryGateScopeRef = useRef("");
  const requestSequenceRef = useRef(0);
  const displayedScopeRef = useRef("");
  const effectiveJobClass = onJobClassChange ? (jobClass || "") : (jobClass || localJobClass);
  const effectiveFrameworkFamily = onFrameworkFamilyChange
    ? (frameworkFamily || "")
    : (frameworkFamily || localFrameworkFamily);
  const callbacksRef = useRef({});
  callbacksRef.current = {
    onJobClassChange,
    onFrameworkFamilyChange,
    onReportingGroupChange,
    onLocationChange,
  };

  const selectJobClass = useCallback((value) => {
    const callback = callbacksRef.current.onJobClassChange;
    if (callback) callback(value);
    else {
      setLocalJobClass(value);
      setLocalFrameworkFamily("");
    }
  }, []);

  const selectFrameworkFamily = useCallback((value) => {
    const callback = callbacksRef.current.onFrameworkFamilyChange;
    if (callback) callback(value);
    else setLocalFrameworkFamily(value);
  }, []);

  const load = useCallback(async ({ requestSummary = true, silent = false, retryFailed = false } = {}) => {
    const requestSequence = ++requestSequenceRef.current;
    const scopeKey = JSON.stringify([
      periodId || "",
      effectiveJobClass || "",
      effectiveFrameworkFamily || "",
      reportingGroupId || "",
      locationId || "",
    ]);
    // Clear the "Summarize this range" intent when the scope itself changes, so
    // a manual request for one window never leaks into the next.
    if (summaryGateScopeRef.current !== scopeKey) {
      summaryRequestedRef.current = false;
      summaryGateScopeRef.current = scopeKey;
    }
    if (!silent) {
      setLoading(true);
      setError("");
      if (displayedScopeRef.current && displayedScopeRef.current !== scopeKey) {
        setData(null);
        setSummaryStatus("idle");
      }
    }
    try {
      const response = await getManagerCoachingDashboard({
        periodId: periodId || undefined,
        jobClass: effectiveJobClass || undefined,
        frameworkFamily: effectiveFrameworkFamily || undefined,
        reportingGroupId: reportingGroupId || undefined,
        locationId: locationId || undefined,
      });
      if (requestSequence !== requestSequenceRef.current) return;
      const availableFamilies = response.available_framework_families || [];
      if (
        effectiveFrameworkFamily &&
        availableFamilies.length > 0 &&
        !availableFamilies.includes(effectiveFrameworkFamily)
      ) {
        // Heal stale bookmarks and URLs created before job-class switches
        // cleared their framework. Do not render or summarize the known-empty
        // incompatible response while the corrected request is loading.
        selectFrameworkFamily(availableFamilies[0]);
        setAvailable(true);
        return;
      }
      setData(response);
      displayedScopeRef.current = scopeKey;
      if (!effectiveJobClass && response.selected_job_class) selectJobClass(response.selected_job_class);
      if (!effectiveFrameworkFamily && response.selected_framework_family) selectFrameworkFamily(response.selected_framework_family);
      if (reportingGroupId && !(response.filters?.reporting_groups || []).some((item) => String(item.id) === String(reportingGroupId))) callbacksRef.current.onReportingGroupChange?.("");
      if (locationId && !(response.filters?.locations || []).some((item) => String(item.id) === String(locationId))) callbacksRef.current.onLocationChange?.("");
      setAvailable(true);
      const summaryEligible = Boolean(
        response.selected_job_class
        && response.selected_framework_family
        && Number(response.aggregate?.scorecard_count || 0) > 0
      );
      // Arbitrary ranges with no pre-generated narrative are summarized only on
      // request: show a prompt instead of auto-firing, until the manager asks.
      const onRequestPending = Boolean(
        summaryEligible
        && response.summary_on_request
        && !response.narrative_summary
        && !summaryRequestedRef.current
      );
      const baseSummaryStatus = !summaryEligible
        ? "idle"
        : !response.narrative_summary
          ? onRequestPending
            ? "on_request"
            : "pending"
          : response.narrative_summary.stale
            ? "stale"
            : "ready";
      setSummaryStatus((current) => (
        !requestSummary
        && (current === "pending" || current === "updating")
        && (baseSummaryStatus === "pending" || baseSummaryStatus === "stale")
          ? current
          : baseSummaryStatus
      ));
      const shouldRefresh = requestSummary
        && summaryEligible
        && !onRequestPending
        && (!response.narrative_summary || response.narrative_summary.stale);
      if (shouldRefresh) {
        try {
          const refresh = await refreshManagerCoachingSummary({
            periodId: response.period?.id,
            jobClass: response.selected_job_class,
            frameworkFamily: response.selected_framework_family,
            reportingGroupId: reportingGroupId || undefined,
            locationId: locationId || undefined,
            retryFailed,
          });
          if (requestSequence !== requestSequenceRef.current) return;
          if (refresh.queued || refresh.reason === "in_progress") {
            setSummaryStatus(response.narrative_summary ? "updating" : "pending");
          } else if (refresh.reason === "previously_failed" && !response.narrative_summary) {
            setSummaryStatus("failed");
          } else if (response.narrative_summary) {
            setSummaryStatus("ready");
          }
        } catch (summaryError) {
          if (requestSequence !== requestSequenceRef.current) return;
          setSummaryStatus(response.narrative_summary ? "stale" : "failed");
        }
      }
    } catch (err) {
      if (requestSequence !== requestSequenceRef.current) return;
      if (err.response?.status === 404) {
        setAvailable(false);
      } else {
        setError(err.response?.data?.detail || "Could not load coaching performance.");
      }
    } finally {
      if (!silent && requestSequence === requestSequenceRef.current) setLoading(false);
    }
  }, [effectiveFrameworkFamily, effectiveJobClass, locationId, periodId, reportingGroupId, selectFrameworkFamily, selectJobClass]);

  useEffect(() => {
    summaryPollCountRef.current = 0;
    load();
  }, [load, refreshVersion]);

  useEffect(() => {
    if (summaryStatus !== "pending" && summaryStatus !== "updating") return undefined;
    const timer = window.setInterval(async () => {
      if (summaryPollInFlightRef.current) return;
      if (summaryPollCountRef.current >= SUMMARY_POLL_LIMIT) {
        setSummaryStatus((current) => current === "updating" ? "stale" : "delayed");
        return;
      }
      summaryPollCountRef.current += 1;
      summaryPollInFlightRef.current = true;
      try {
        // The refresh endpoint is idempotent and reports terminal work status.
        // Poll it as well as the dashboard so failures surface immediately.
        await load({ requestSummary: true, silent: true });
      } finally {
        summaryPollInFlightRef.current = false;
      }
    }, SUMMARY_POLL_INTERVAL_MS);
    return () => window.clearInterval(timer);
  }, [load, summaryStatus]);

  useEffect(() => {
    const anchorId = decodeURIComponent((location.hash || "").replace(/^#/, ""));
    if (!anchorId.startsWith("manager-coaching-citation-")) return undefined;
    const frame = window.requestAnimationFrame(() => {
      const target = document.getElementById(anchorId);
      if (target && typeof target.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center" });
      }
    });
    return () => window.cancelAnimationFrame(frame);
  }, [data?.narrative_summary, loading, location.hash]);

  const stages = useMemo(
    () => (data?.aggregate?.dimensions || []).filter((item) => item.dimension_type === "stage"),
    [data]
  );
  const narrativeOutput = data?.narrative_summary?.output || {};
  const strengthItems = narrativeOutput.strengths || [];
  const opportunityItems = narrativeOutput.opportunities || [];
  const coachingRoles = new Set((data?.employees || []).map((employee) => employee.coaching_role || ""));
  const showCoachingRole = coachingRoles.size > 1;
  const comparisonDescription = comparisonPeriodDescription(data?.period);
  if (!available) return null;

  const openEmployee = (employeeId) => {
    const params = new URLSearchParams();
    if (data?.period?.id) params.set("period", data.period.id);
    if (data?.selected_job_class) params.set("job_class", data.selected_job_class);
    if (data?.selected_framework_family) params.set("framework", data.selected_framework_family);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    navigate(`/manager/employees/${employeeId}/engagement?${params.toString()}`);
  };

  const openDimension = (dimension) => {
    const params = new URLSearchParams();
    if (data?.period?.id) params.set("period", data.period.id);
    if (data?.selected_job_class) params.set("job_class", data.selected_job_class);
    if (data?.selected_framework_family) params.set("framework", data.selected_framework_family);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    navigate(`/manager/coaching/dimensions/${dimension.dimension_type}/${encodeURIComponent(dimension.dimension_id)}?${params.toString()}`);
  };

  const openEvidence = (citation, returnAnchor) => {
    if (!citation?.employee_id || !citation?.thread_id || !citation?.conversation_id) return;
    const params = new URLSearchParams();
    if (data?.period?.id) params.set("period", data.period.id);
    if (data?.selected_job_class) params.set("job_class", data.selected_job_class);
    if (data?.selected_framework_family) params.set("framework", data.selected_framework_family);
    if (reportingGroupId) params.set("reporting_group", reportingGroupId);
    if (locationId) params.set("location", locationId);
    params.set("conversation", citation.conversation_id);
    params.set("evidence", citation.evidence_id);
    params.set("return_view", "manager_coaching");
    if (returnAnchor) params.set("return_anchor", returnAnchor);
    if (citation.anchor?.detail_item_id != null) params.set("detail_item", citation.anchor.detail_item_id);
    if (citation.anchor?.turn_index != null) params.set("turn", citation.anchor.turn_index);
    if (returnAnchor) {
      navigate(`${location.pathname}${location.search}#${encodeURIComponent(returnAnchor)}`, { replace: true });
    }
    navigate(`/manager/employees/${citation.employee_id}/threads/${citation.thread_id}?${params.toString()}`);
  };

  const retrySummary = () => {
    summaryPollCountRef.current = 0;
    setSummaryStatus(data?.narrative_summary ? "updating" : "pending");
    load({ requestSummary: true, silent: true, retryFailed: true });
  };

  // On-request generation for arbitrary ranges: open the gate, then reuse the
  // exact same refresh + poll path the calendar periods take automatically.
  const summarizeRange = () => {
    summaryRequestedRef.current = true;
    summaryPollCountRef.current = 0;
    setSummaryStatus("pending");
    load({ requestSummary: true, silent: true });
  };

  return (
    <VStack id="coaching" align="stretch" spacing={5} aria-label="Coaching performance" scrollMarginTop="80px">
      <Flex justify="space-between" align={{ base: "flex-start", md: "center" }} gap={4} direction={{ base: "column", md: "row" }}>
        <Box>
          <Heading size="lg">Team performance</Heading>
          <Text mt={1} color="gray.500">Role-specific coaching results, grounded in analyzed customer conversations.</Text>
        </Box>
        <HStack spacing={1} bg="#EAECF0" p={1} borderRadius="lg" wrap="wrap">
          {(data?.available_job_classes || []).map((value) => (
            <Button
              key={value}
              size="sm"
              variant="ghost"
              bg={data?.selected_job_class === value ? "white" : "transparent"}
              color={data?.selected_job_class === value ? "blue.600" : "gray.700"}
              boxShadow={data?.selected_job_class === value ? "sm" : "none"}
              _hover={{ bg: data?.selected_job_class === value ? "white" : "whiteAlpha.600" }}
              onClick={() => selectJobClass(value)}
            >
              {JOB_LABELS[value] || value}
            </Button>
          ))}
        </HStack>
      </Flex>

      <Flex gap={3} direction={{ base: "column", md: "row" }}>
        <Select value={reportingGroupId || ""} onChange={(event) => onReportingGroupChange?.(event.target.value)} aria-label="Reporting group" bg="white" maxW={{ md: "360px" }}>
          <option value="">All assigned reporting groups</option>
          {(data?.filters?.reporting_groups || []).map((group) => <option key={group.id} value={group.id}>{group.name}</option>)}
        </Select>
        <Select value={locationId || ""} onChange={(event) => onLocationChange?.(event.target.value)} aria-label="Dealership location" bg="white" maxW={{ md: "360px" }}>
          <option value="">All authorized locations</option>
          {(data?.filters?.locations || []).map((location) => <option key={location.id} value={location.id}>{location.name}</option>)}
        </Select>
        {(data?.available_framework_families || []).length > 1 && (
          <Select value={data?.selected_framework_family || effectiveFrameworkFamily || ""} onChange={(event) => selectFrameworkFamily(event.target.value)} aria-label="Coaching framework" bg="white" maxW={{ md: "280px" }}>
            <option value="">All frameworks</option>
            {(data.available_framework_families || []).map((family) => <option key={family} value={family}>{family}</option>)}
          </Select>
        )}
      </Flex>

      {error && <Alert status="error" borderRadius="lg"><AlertIcon />{error}</Alert>}
      {loading && !data ? (
        <Grid templateColumns={{ base: "1fr", md: "repeat(2, 1fr)", xl: "repeat(4, 1fr)" }} gap={4}>
          {[0, 1, 2, 3].map((item) => <Skeleton key={item} h="188px" borderRadius="xl" />)}
        </Grid>
      ) : data?.selected_job_class ? (
        <>
          <Grid templateColumns={{ base: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" }} gap={0} bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflow="hidden">
            {[
              ["Customer conversations", data.aggregate.scorecard_count, "Analyzed in this period"],
              ["Team score", percentage(data.aggregate.score), "Applicable behaviors only"],
              ["Avg duration", formatDuration(data.aggregate.avg_duration_ms), "Mean conversation talk-time"],
              ["Employees coached", data.employees.length, JOB_LABELS[data.selected_job_class]],
            ].map(([label, value, help], index) => (
              <Box key={label} px={5} py={4} borderLeftWidth={{ base: 0, md: index ? "1px" : 0 }} borderTopWidth={{ base: index ? "1px" : 0, md: 0 }} borderColor="gray.200">
                <Text fontSize="xs" color="gray.500" fontWeight="bold" textTransform="uppercase" letterSpacing="wide">{label}</Text>
                <Flex align="baseline" justify="space-between" mt={1} gap={2}><Text fontSize="2xl" fontWeight="bold">{value}</Text><Text fontSize="xs" color="gray.500">{help}</Text></Flex>
              </Box>
            ))}
          </Grid>

          {Number(data.aggregate?.short_conversation_count) > 0 && (
            <Flex mt={-1} align="center" gap={2} wrap="wrap">
              <ShortConversationChip count={data.aggregate.short_conversation_count} />
              <Text fontSize="xs" color="gray.500">
                Scored on {data.aggregate.scored_conversation_count ?? data.aggregate.scorecard_count} conversation{Number(data.aggregate.scored_conversation_count ?? data.aggregate.scorecard_count) === 1 ? "" : "s"}.
              </Text>
            </Flex>
          )}

          {stages.length > 0 && (
            <Grid templateColumns={{ base: "1fr", md: "repeat(3, minmax(0, 1fr))" }} gap={3}>
              {stages.map((dimension) => <MetricOverviewCard key={dimension.dimension_id} dimension={dimension} period={data.period} onOpen={() => openDimension(dimension)} />)}
            </Grid>
          )}

          {summaryStatus === "on_request" && Number(data.aggregate?.scorecard_count || 0) > 0 && data.selected_framework_family && (
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" px={{ base: 4, md: 5 }} py={4} boxShadow="0 1px 2px rgba(16,24,40,.04)">
              <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap={4} direction={{ base: "column", sm: "row" }}>
                <HStack align="flex-start" spacing={3}>
                  <Flex boxSize="34px" borderRadius="lg" bg="blue.50" color="blue.600" align="center" justify="center" flex="0 0 auto">
                    <Icon as={MdAutoAwesome} boxSize={4} />
                  </Flex>
                  <Box>
                    <Text fontWeight="bold" color="gray.900">Summarize this range</Text>
                    <Text mt={1} color="gray.600" fontSize="sm" maxW="640px">
                      Coaching insights aren’t pre-generated for custom date ranges. Generate a narrative for the selected window on demand.
                    </Text>
                  </Box>
                </HStack>
                <Button size="sm" colorScheme="blue" leftIcon={<MdAutoAwesome />} onClick={summarizeRange} flex="0 0 auto">Summarize this range</Button>
              </Flex>
            </Box>
          )}

          {!data.narrative_summary && summaryStatus !== "on_request" && Number(data.aggregate?.scorecard_count || 0) > 0 && data.selected_framework_family && (
            <Box bgGradient="linear(110deg, #347DE9 0%, #6859E9 52%, #9A4DE2 100%)" borderRadius="xl" p="1px" boxShadow="0 8px 28px rgba(62,91,208,.16)">
              <Box bg="white" borderRadius="xl" px={{ base: 5, md: 7 }} py={{ base: 6, md: 7 }}>
                <Flex align={{ base: "flex-start", sm: "center" }} justify="space-between" gap={5} direction={{ base: "column", sm: "row" }}>
                  <HStack align="flex-start" spacing={4}>
                    <Flex boxSize="42px" borderRadius="xl" bg="blue.50" color="blue.600" align="center" justify="center" flex="0 0 auto">
                      {summaryStatus === "pending" ? <Spinner size="sm" thickness="3px" /> : <Icon as={MdAutoAwesome} boxSize={5} />}
                    </Flex>
                    <Box>
                      <Heading size="md" color="gray.900">
                        {summaryStatus === "failed" ? "Coaching insights couldn’t be updated" : summaryStatus === "delayed" ? "Team coaching insights are still being prepared" : "Preparing team coaching insights"}
                      </Heading>
                      <Text mt={2} color="gray.600" fontSize="sm" maxW="720px">
                        {summaryStatus === "failed"
                          ? "Performance data remains available. Try generating the narrative again."
                          : summaryStatus === "delayed"
                            ? "This is taking longer than expected. You can continue using the dashboard and check again later."
                            : "We’re analyzing the current coaching results for this team. This section will update automatically—you can continue using the dashboard."}
                      </Text>
                    </Box>
                  </HStack>
                  {(summaryStatus === "failed" || summaryStatus === "delayed") && <Button size="sm" colorScheme="blue" onClick={retrySummary}>Check again</Button>}
                </Flex>
              </Box>
            </Box>
          )}

          {data.narrative_summary && (
            <Box bgGradient="linear(110deg, #347DE9 0%, #6859E9 52%, #9A4DE2 100%)" borderRadius="xl" p="1px" boxShadow="0 8px 28px rgba(62,91,208,.16)">
              <Box bgGradient="linear(110deg, #347DE9 0%, #6859E9 52%, #9A4DE2 100%)" color="white" px={5} py={4} borderTopRadius="xl">
                <Flex align="center" justify="space-between" gap={3}>
                  <HStack><Icon as={MdAutoAwesome} boxSize={6} /><Heading size="md">Manager coaching</Heading></HStack>
                  {(summaryStatus === "updating" || summaryStatus === "stale") && (
                    <Badge bg="whiteAlpha.300" color="white">{summaryStatus === "stale" ? "Update delayed" : "Updating"}</Badge>
                  )}
                </Flex>
              </Box>
              <Grid bg="white" borderBottomRadius="xl" p={4} templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={3}>
                <InsightCard title="Team Strengths" items={strengthItems} icon={MdTrendingUp} accent="#00B96B" empty="Not enough comparable evidence yet." onOpenEvidence={openEvidence} />
                <InsightCard title="Opportunity Areas" items={opportunityItems} icon={MdOutlineLightbulb} accent="#F47B20" empty="No repeated opportunity is stable yet." onOpenEvidence={openEvidence} />
                <InsightCard title="Notable Momentum" items={narrativeOutput.improving || []} icon={MdTrendingUp} accent="#1D5DEE" empty="No stable movement yet." onOpenEvidence={openEvidence} />
                <InsightCard title="Manager Next Steps" items={narrativeOutput.actions || []} icon={MdOutlineFlag} accent="#7A4CE0" empty="No suggested actions yet." onOpenEvidence={openEvidence} />
              </Grid>
            </Box>
          )}

          <Box id="team" scrollMarginTop="80px">
            <Flex justify="space-between" align="end" mb={3} gap={4} wrap="wrap">
              <Box><Heading size="md">Employees</Heading><Text mt={1} fontSize="sm" color="gray.500">Review individual coaching performance and source customer conversations.</Text></Box>
              <Text fontSize="sm" color="gray.500">{data.employees.length} in this view</Text>
            </Flex>
            <Box bg="white" borderWidth="1px" borderColor="gray.200" borderRadius="xl" overflowX="auto" boxShadow="0 1px 2px rgba(16,24,40,.04)">
              <Table size="sm">
                <Thead><Tr><Th py={4}>Employee</Th>{showCoachingRole && <Th>Coaching role</Th>}<Th isNumeric>Score</Th><Th><HStack spacing={1}><Text>Trend</Text><Tooltip label={comparisonDescription} hasArrow><Box as="span" color="gray.400" aria-label="About score trend"><MdInfoOutline /></Box></Tooltip></HStack></Th><Th isNumeric>Avg duration</Th>{stages.map((stage) => <Th key={stage.dimension_id} isNumeric minW="112px">{stageDisplayLabel(stage.label)}</Th>)}<Th aria-label="Open" /></Tr></Thead>
                <Tbody>
                  {data.employees.map((employee) => {
                    const TrendIcon = employee.trend > 0 ? MdTrendingUp : employee.trend < 0 ? MdTrendingDown : MdTrendingFlat;
                    return (
                      <Tr key={employee.id} cursor="pointer" _hover={{ bg: "#F7FAFF" }} onClick={() => openEmployee(employee.id)}>
                        <Td py={4}>
                          <HStack align="start">
                            <Avatar size="sm" name={employee.name} bg="blue.100" color="blue.700" />
                            <VStack align="start" spacing={1}>
                              <Text fontWeight="bold">{employee.name}</Text>
                              {Number(employee.short_conversation_count) > 0 && (
                                <Box onClick={(event) => event.stopPropagation()}>
                                  <ShortConversationChip count={employee.short_conversation_count} />
                                </Box>
                              )}
                            </VStack>
                          </HStack>
                        </Td>
                        {showCoachingRole && <Td><Badge variant="subtle" colorScheme="blue" borderRadius="full" px={2.5}>{employee.coaching_role || JOB_LABELS[data.selected_job_class]}</Badge></Td>}
                        <Td isNumeric><HStack justify="flex-end"><Rating score={employee.score} compact /><Text fontWeight="bold">{percentage(employee.score)}</Text></HStack></Td>
                        <Td><Tooltip label={comparisonDescription} hasArrow><HStack color={employee.trend > 0 ? "green.600" : employee.trend < 0 ? "red.500" : "gray.500"}>{employee.trend != null && <Icon as={TrendIcon} />}<Text fontSize="xs">{employee.trend == null ? "—" : trendLabel(employee.trend)}</Text></HStack></Tooltip></Td>
                        <Td isNumeric><Text fontSize="sm" color="gray.600">{formatDuration(employee.avg_duration_ms)}</Text></Td>
                        {stages.map((stage) => {
                          const result = (employee.dimensions || []).find((item) => item.dimension_id === stage.dimension_id);
                          return <Td key={stage.dimension_id} isNumeric><Text fontWeight="semibold">{percentage(result?.score)}</Text><Tooltip label={comparisonDescription} hasArrow><Text fontSize="xs" color={result?.trend > 0 ? "green.600" : result?.trend < 0 ? "red.500" : "gray.500"}>{result?.trend == null ? "—" : trendLabel(result.trend)}</Text></Tooltip></Td>;
                        })}
                        <Td><Button size="sm" variant="ghost" aria-label={`Review ${employee.name}`} onClick={(event) => { event.stopPropagation(); openEmployee(employee.id); }}><MdChevronRight /></Button></Td>
                      </Tr>
                    );
                  })}
                </Tbody>
              </Table>
              {!data.employees.length && <Text p={8} color="gray.500">No coaching reports for this role and period.</Text>}
            </Box>
          </Box>

        </>
      ) : (
        <Box bg="white" borderWidth="1px" borderRadius="xl" p={8}>
          <Heading size="sm">No coaching reports in this period</Heading>
          <Text mt={2} color="gray.500">Operational activity remains available below.</Text>
        </Box>
      )}
    </VStack>
  );
}
